/**
 * What the differences between a material order and its quote do to the job's
 * margin.
 *
 * A difference in dollars is not the whole of it: material is taxed on the
 * order, and the margin the job was bid at is gross profit over the contract.
 * So this runs the totals ladder twice, unchanged — once with the order as it
 * stands and once with its lines as the quote has them — and reports both.
 * Nothing is re-derived here; every figure is `computeLadder`'s (CLAUDE.md
 * rules 1 and 3).
 */

import { computeLadder, type LadderInput } from "./ladder.js";
import { subCents, toDecimal, type Cents } from "./money.js";
import type { GeneratedLine } from "./generator.js";
import type { CostDocument } from "./types.js";

export interface QuoteMarginImpact {
  /** Gross profit over the contract, as ordered and as quoted. */
  marginAsOrdered: number;
  marginAsQuoted: number;
  grossProfitAsOrdered: Cents;
  grossProfitAsQuoted: Cents;
  /** As ordered minus as quoted. Negative means the order costs the job profit. */
  grossProfitImpact: Cents;
}

/**
 * The ladder for the estimate, with one material order's lines swapped out.
 *
 * `orderId` picks the order within `ladder.materialOrders`; its tax rate and
 * reconciliation stay as they are, so the only thing that differs between the
 * two runs is the lines. Null when the estimate has no contract to take a
 * margin of, or the order is not among its orders.
 *
 * `checkedWith` are the sibling orders the check read this one with
 * (`checkedWith` on a compared result). The quote was compared against all of
 * them together, so "as quoted" puts the quoted lines on this order and takes
 * the siblings' lines away — otherwise their parts would be counted twice.
 */
export function quoteMarginImpact(
  ladder: Omit<LadderInput, "materialOrders"> & {
    materialOrders: readonly (CostDocument & { id?: string })[];
  },
  orderId: string,
  /** The order's lines as they are on screen, which may be newer than `ladder`. */
  orderedItems: readonly { qty: unknown; unit_price: unknown }[],
  /** `expected` from a compared quote check. */
  quotedLines: readonly Pick<GeneratedLine, "qty" | "unitPrice">[],
  checkedWith: readonly string[] = [],
): QuoteMarginImpact | null {
  if (!ladder.materialOrders.some((o) => o.id === orderId)) return null;
  const siblings = new Set(checkedWith);

  const withLines = (
    items: readonly { qty: unknown; unit_price: unknown }[],
    emptySiblings: boolean,
  ) =>
    computeLadder({
      ...ladder,
      materialOrders: ladder.materialOrders.map((o) =>
        o.id === orderId
          ? { ...o, items }
          : emptySiblings && o.id && siblings.has(o.id)
            ? { ...o, items: [] }
            : o,
      ),
    } as LadderInput);

  const asOrdered = withLines(orderedItems, false);
  if (asOrdered.grandTotal <= 0) return null;
  const asQuoted = withLines(
    quotedLines.map((line) => ({
      qty: line.qty,
      unit_price: toDecimal(line.unitPrice),
    })),
    true,
  );

  return {
    marginAsOrdered: asOrdered.margin,
    marginAsQuoted: asQuoted.margin,
    grossProfitAsOrdered: asOrdered.grossProfit,
    grossProfitAsQuoted: asQuoted.grossProfit,
    grossProfitImpact: subCents(asOrdered.grossProfit, asQuoted.grossProfit),
  };
}
